import React, { useEffect, useState } from 'react'
import { useGetMeQuery } from '../../graphql/api/queries/GetMe.generated'
import { LoadingCircular } from '../../components/LoadingCircular'
import LoadingError from '../../components/LoadingError'
import { UserProfile } from '../../userprofile/types/preferences'
import { UserPreferencesContext } from './UserPreferencesContext'
import { User } from '../../userprofile/types/user'
import { useDispatch } from 'react-redux'
import { readPersistedItemsPerPage, setItemsPerPage } from '../../store/preferences/slicer'
import { monitorUserLoggedIn, trackUserLoggedIn } from '../../userprofile/tracking'
import ErrorState from '../../components/ErrorState/ErrorState'
import { extractUserProfile } from './userPreferences'

type PreferencesProviderProps = {
  readonly isAuthenticated: boolean
  readonly user: User
  readonly children: React.ReactNode
}

export const UserPreferencesProvider: React.FC<PreferencesProviderProps> = ({ user, isAuthenticated, children }) => {
  const [error, setError] = useState<Error | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const dispatch = useDispatch()
  const { loading, refetch } = useGetMeQuery({
    skip: !isAuthenticated,
    onError: setError,
    onCompleted: (data) => setUserProfile(extractUserProfile(data)),
  })

  useEffect(() => {
    dispatch(setItemsPerPage(readPersistedItemsPerPage()))
  }, [dispatch])

  useEffect(() => {
    if (userProfile) {
      trackUserLoggedIn(
        user,
        userProfile.userId,
        userProfile.email || '',
        userProfile.name || '',
        userProfile.country?.name || '',
        userProfile.region?.name || '',
      ).then(() => monitorUserLoggedIn(user, userProfile.role))
    }
  }, [user, userProfile])

  if (loading) return <LoadingCircular />

  if (error) return <LoadingError errorText={error.message} />

  if (!userProfile) return <ErrorState onRefetch={refetch} />

  return (
    <UserPreferencesContext.Provider value={{ ...userProfile, refetchUserPreferences: refetch }}>
      {children}
    </UserPreferencesContext.Provider>
  )
}
